/**
 * Branding Context Provider
 * Provides organization branding state throughout the application
 * Automatically fetches branding based on subdomain and applies CSS variables
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getOrgSlugFromUrl, isOrgPortal as checkIsOrgPortal } from '../utils/subdomain';
import {
    fetchOrgBranding,
    applyBrandingColors,
    updateFavicon,
    updatePageTitle,
    DEFAULT_BRANDING,
} from '../../lender/api/branding';
import { setOrgContext } from '../../lender/api/client';

// Create the context
const BrandingContext = createContext(undefined);

/**
 * BrandingProvider Component
 * Wraps the application and provides branding state to all children
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(DEFAULT_BRANDING);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orgSlug, setOrgSlug] = useState(null);
    const [orgId, setOrgId] = useState(null);

    // Check if current URL is an organization portal
    const isOrgPortal = checkIsOrgPortal();

    // Fetch and apply branding on mount
    useEffect(() => {
        const initBranding = async () => {
            const slug = getOrgSlugFromUrl();
            setOrgSlug(slug);

            if (!slug) {
                // Not on an org portal, use default branding
                setBranding(DEFAULT_BRANDING);
                applyBrandingColors(DEFAULT_BRANDING);
                updatePageTitle(DEFAULT_BRANDING.companyName);
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);

                const brandingData = await fetchOrgBranding(slug);

                setBranding(brandingData);
                setOrgId(brandingData.orgId || null);

                // Set org context for API calls
                if (brandingData.orgId) {
                    setOrgContext(brandingData.orgId);
                }

                // Apply branding to the DOM
                applyBrandingColors(brandingData);
                updatePageTitle(brandingData.companyName);

                if (brandingData.faviconUrl) {
                    updateFavicon(brandingData.faviconUrl);
                }
            } catch (err) {
                console.error('Error initializing branding:', err);
                setError(err.message || 'Failed to load branding');
                // Fall back to default branding
                setBranding(DEFAULT_BRANDING);
                applyBrandingColors(DEFAULT_BRANDING);
            } finally {
                setIsLoading(false);
            }
        };

        initBranding();
    }, []);

    // Refresh branding (useful after login if org context changes)
    const refreshBranding = useCallback(async () => {
        const slug = orgSlug || getOrgSlugFromUrl();
        if (!slug) {
            return;
        }

        try {
            const brandingData = await fetchOrgBranding(slug);
            setBranding(brandingData);

            if (brandingData.orgId) {
                setOrgId(brandingData.orgId);
                setOrgContext(brandingData.orgId);
            }

            applyBrandingColors(brandingData);
            updatePageTitle(brandingData.companyName);

            if (brandingData.faviconUrl) {
                updateFavicon(brandingData.faviconUrl);
            }
        } catch (err) {
            console.error('Error refreshing branding:', err);
        }
    }, [orgSlug]);

    const contextValue = {
        branding,
        isLoading,
        error,
        isOrgPortal,
        orgSlug,
        orgId,
        refreshBranding,
    };

    return (
        <BrandingContext.Provider value={contextValue}>
            {children}
        </BrandingContext.Provider>
    );
};

/**
 * Hook to access branding context
 * Must be used within a BrandingProvider
 *
 * @returns {Object} Branding context value
 * @property {Object} branding - The current branding configuration
 * @property {boolean} isLoading - Whether branding is being loaded
 * @property {string|null} error - Error message if branding fetch failed
 * @property {boolean} isOrgPortal - Whether the current URL is an org portal
 * @property {string|null} orgSlug - The organization slug from URL
 * @property {string|null} orgId - The organization ID (after branding is loaded)
 * @property {Function} refreshBranding - Function to refresh branding data
 */
export const useBranding = () => {
    const context = useContext(BrandingContext);
    if (context === undefined) {
        throw new Error('useBranding must be used within a BrandingProvider');
    }
    return context;
};

export default BrandingContext;
